import { useEffect, useMemo, useState } from "react";
import s from "./TeacherSectionsPanel.module.css";

/* Teacher dashboard: Grade Level (tabs) → Section (chips) → that section's
 * Subjects and Students side by side. Students belong to the SECTION (every
 * subject in it shares the same roster), so they sit next to the subject
 * list rather than repeated under each subject. */

const AVATAR_COLORS = ["#5a4e3a", "#3a4e4a", "#4a3a5a", "#4e4a3a", "#3a4a5a", "#5a3a3a", "#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function studentName(st) {
  const first = (st?.firstName || "").trim();
  const last  = (st?.lastName  || "").trim();
  if (first && last) return `${first} ${last}`;
  return first || last || st?.studentNumber || "—";
}
function byGradeOrder(a, b) {
  return (a.ordering ?? 0) - (b.ordering ?? 0) || (a.name || "").localeCompare(b.name || "");
}

const NO_GRADE = "none";

export default function TeacherSectionsPanel({
  teacherStaffId,
  sections, gradeLevels, students, enrollments, offerings,
  subjectsById, employeesById, roomsById,
  activeSy,
  onOpenRoster, onOpenStudent, onAttendance, onGrades,
}) {
  const gradeById = useMemo(() => Object.fromEntries(gradeLevels.map(g => [g.id, g])), [gradeLevels]);

  /* ── Students per section ─────────────────────────────────────────── */
  // Enrolled students for the active school year, plus anyone whose
  // currentSectionId points here (covers schools that never created
  // enrollment rows). Deduped per section.
  const studentsBySection = useMemo(() => {
    const studentById = Object.fromEntries(students.map(st => [st.id, st]));
    const map = new Map(); // sectionId -> Map<studentId, student>
    const add = (secId, st) => {
      if (secId == null || !st) return;
      if (!map.has(secId)) map.set(secId, new Map());
      map.get(secId).set(st.id, st);
    };
    for (const e of enrollments) {
      const status = String(e.status || "").toUpperCase();
      if (status && status !== "ENROLLED") continue;
      if (activeSy && e.schoolYearId != null && String(e.schoolYearId) !== String(activeSy.id)) continue;
      add(e.sectionId, studentById[e.studentId]);
    }
    for (const st of students) add(st.currentSectionId, st);
    const out = new Map();
    for (const [secId, m] of map) {
      out.set(secId, [...m.values()].sort((a, b) => studentName(a).localeCompare(studentName(b))));
    }
    return out;
  }, [students, enrollments, activeSy]);

  /* ── Offerings per section ────────────────────────────────────────── */
  const offeringsBySection = useMemo(() => {
    const map = new Map();
    for (const o of offerings) {
      if (!map.has(o.sectionId)) map.set(o.sectionId, []);
      map.get(o.sectionId).push(o);
    }
    for (const list of map.values()) {
      // The teacher's own classes first, then alphabetical by subject.
      list.sort((a, b) => {
        const mineA = String(a.teacherStaffId) === String(teacherStaffId);
        const mineB = String(b.teacherStaffId) === String(teacherStaffId);
        if (mineA !== mineB) return mineA ? -1 : 1;
        return (subjectsById[a.subjectId]?.title || "").localeCompare(subjectsById[b.subjectId]?.title || "");
      });
    }
    return map;
  }, [offerings, subjectsById, teacherStaffId]);

  /* ── Grade tabs ───────────────────────────────────────────────────── */
  const gradeTabs = useMemo(() => {
    const secsByGrade = new Map();
    for (const sec of sections) {
      const key = sec.gradeLevelId != null && gradeById[sec.gradeLevelId] ? sec.gradeLevelId : NO_GRADE;
      if (!secsByGrade.has(key)) secsByGrade.set(key, []);
      secsByGrade.get(key).push(sec);
    }
    const tabs = gradeLevels.slice().sort(byGradeOrder)
      .filter(gl => secsByGrade.has(gl.id))
      .map(gl => ({ key: String(gl.id), name: gl.name, sections: secsByGrade.get(gl.id) }));
    if (secsByGrade.has(NO_GRADE)) tabs.push({ key: NO_GRADE, name: "Other", sections: secsByGrade.get(NO_GRADE) });
    for (const t of tabs) t.sections.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return tabs;
  }, [sections, gradeLevels, gradeById]);

  const [gradeKey,  setGradeKey]  = useState("all");
  const [sectionId, setSectionId] = useState(null);
  const [search,    setSearch]    = useState("");

  const activeTab = gradeTabs.find(t => t.key === gradeKey) || null;

  // Picking a grade tab selects its first section (so a grade with one
  // section opens straight away); a stale selection is dropped.
  useEffect(() => {
    if (!activeTab) { setSectionId(null); return; }
    if (!activeTab.sections.some(x => x.id === sectionId)) setSectionId(activeTab.sections[0]?.id ?? null);
  }, [activeTab, sectionId]);

  useEffect(() => { setSearch(""); }, [sectionId]);

  function roleOf(sec) {
    const adviser = String(sec.adviserStaffId) === String(teacherStaffId);
    const mine = (offeringsBySection.get(sec.id) || [])
      .filter(o => String(o.teacherStaffId) === String(teacherStaffId)).length;
    const bits = [];
    if (adviser) bits.push("Form adviser");
    if (mine > 0) bits.push(`You teach ${mine} subject${mine === 1 ? "" : "s"}`);
    return bits.join(" · ") || "Section";
  }

  function openSection(sec) {
    const key = sec.gradeLevelId != null && gradeById[sec.gradeLevelId] ? String(sec.gradeLevelId) : NO_GRADE;
    setGradeKey(key);
    setSectionId(sec.id);
  }

  const totalStudents = useMemo(() => {
    const ids = new Set();
    for (const sec of sections) for (const st of (studentsBySection.get(sec.id) || [])) ids.add(st.id);
    return ids.size;
  }, [sections, studentsBySection]);

  if (sections.length === 0) {
    return <div className={s.empty}>No sections assigned yet.</div>;
  }

  const section = activeTab ? activeTab.sections.find(x => x.id === sectionId) : null;

  return (
    <div className={s.wrap}>
      {/* ── Grade level tabs (scroll sideways when there are many) ── */}
      <div className={s.tabBar} role="tablist" aria-label="Grade level">
        <button type="button" role="tab" aria-selected={gradeKey === "all"}
                className={`${s.tab} ${gradeKey === "all" ? s.tabActive : ""}`}
                onClick={() => setGradeKey("all")}>
          All
          <span className={s.tabCount}>{totalStudents}</span>
        </button>
        {gradeTabs.map(t => {
          const count = t.sections.reduce((n, sec) => n + (studentsBySection.get(sec.id)?.length || 0), 0);
          return (
            <button key={t.key} type="button" role="tab" aria-selected={gradeKey === t.key}
                    className={`${s.tab} ${gradeKey === t.key ? s.tabActive : ""}`}
                    onClick={() => setGradeKey(t.key)}>
              {t.name}
              <span className={s.tabCount}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* ── All: every section as a card, grouped by grade ── */}
      {gradeKey === "all" && (
        <div className={s.overview}>
          {gradeTabs.map(t => (
            <div key={t.key} className={s.overviewGroup}>
              <div className={s.overviewHead}>{t.name}</div>
              <div className={s.cardGrid}>
                {t.sections.map(sec => {
                  const studs = studentsBySection.get(sec.id)?.length || 0;
                  const subs  = offeringsBySection.get(sec.id)?.length || 0;
                  return (
                    <button key={sec.id} type="button" className={s.secCard} onClick={() => openSection(sec)}>
                      <span className={s.secCardRole}>{roleOf(sec)}</span>
                      <span className={s.secCardName}>{sec.name}</span>
                      <span className={s.secCardPills}>
                        <span className={s.pillStudents}>{studs} student{studs === 1 ? "" : "s"}</span>
                        <span className={s.pill}>{subs} subject{subs === 1 ? "" : "s"}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── One grade: section chips + the selected section ── */}
      {activeTab && (
        <>
          {activeTab.sections.length > 1 && (
            <div className={s.chipRow} role="tablist" aria-label="Section">
              {activeTab.sections.map(sec => (
                <button key={sec.id} type="button" role="tab" aria-selected={sec.id === sectionId}
                        className={`${s.chip} ${sec.id === sectionId ? s.chipActive : ""}`}
                        onClick={() => setSectionId(sec.id)}>
                  {sec.name}
                  <span className={s.chipCount}>{studentsBySection.get(sec.id)?.length || 0}</span>
                </button>
              ))}
            </div>
          )}

          {section && (
            <SectionDetail
              key={section.id}
              section={section}
              grade={gradeById[section.gradeLevelId]}
              roleLabel={roleOf(section)}
              students={studentsBySection.get(section.id) || []}
              offerings={offeringsBySection.get(section.id) || []}
              teacherStaffId={teacherStaffId}
              subjectsById={subjectsById}
              employeesById={employeesById}
              roomsById={roomsById}
              search={search}
              setSearch={setSearch}
              onOpenRoster={onOpenRoster}
              onOpenStudent={onOpenStudent}
              onAttendance={onAttendance}
              onGrades={onGrades}
            />
          )}
        </>
      )}
    </div>
  );
}

function SectionDetail({
  section, grade, roleLabel, students, offerings, teacherStaffId,
  subjectsById, employeesById, roomsById, search, setSearch,
  onOpenRoster, onOpenStudent, onAttendance, onGrades,
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter(st =>
      studentName(st).toLowerCase().includes(q) || (st.studentNumber || "").toLowerCase().includes(q));
  }, [students, search]);

  return (
    <div className={s.detail}>
      <div className={s.detailHead}>
        <div>
          <div className={s.detailTitle}>{section.name}</div>
          <div className={s.detailSub}>
            {[grade?.name, roleLabel, `${students.length} student${students.length === 1 ? "" : "s"}`]
              .filter(Boolean).join(" · ")}
          </div>
        </div>
        <button type="button" className={s.linkBtn} onClick={() => onOpenRoster(section.id)}>
          View roster →
        </button>
      </div>

      <div className={s.columns}>
        {/* Subjects */}
        <div className={s.col}>
          <div className={s.colHead}>
            Subjects <span className={s.colCount}>{offerings.length}</span>
          </div>
          <div className={s.scroll}>
            {offerings.length === 0 ? (
              <div className={s.colEmpty}>No subjects scheduled for this section yet.</div>
            ) : offerings.map(o => {
              const subj = subjectsById[o.subjectId];
              const mine = String(o.teacherStaffId) === String(teacherStaffId);
              const teacher = o.teacherStaffId != null ? employeesById[o.teacherStaffId] : null;
              const room = (o.roomId != null && roomsById[o.roomId]?.name) || o.room || "";
              return (
                <div key={o.id} className={`${s.subjRow} ${mine ? s.subjRowMine : ""}`}>
                  <span className={s.subjCode}>{subj?.code || "—"}</span>
                  <div className={s.subjMain}>
                    <div className={s.subjTitle}>
                      {subj?.title || `Subject #${o.subjectId}`}
                      {mine && <span className={s.youBadge}>You</span>}
                    </div>
                    <div className={s.subjMeta}>
                      {mine ? null : <span>{teacher?.name || "No teacher yet"}</span>}
                      {o.schedule && <span>{o.schedule}</span>}
                      {room && <span>Room {room}</span>}
                    </div>
                    {mine && (
                      <div className={s.subjActions}>
                        <button type="button" className={`${s.actBtn} ${s.actAttendance}`} onClick={() => onAttendance(o)}>
                          ✓ Attendance
                        </button>
                        <button type="button" className={`${s.actBtn} ${s.actGrades}`} onClick={() => onGrades(o)}>
                          Encode grades →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Students */}
        <div className={s.col}>
          <div className={s.colHead}>
            Students <span className={s.colCount}>{students.length}</span>
          </div>
          <div className={s.searchWrap}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                 className={s.searchIcon}>
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input className={s.searchInput} value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Search name or student number…" aria-label="Search students" />
          </div>
          <div className={s.scroll}>
            {filtered.length === 0 ? (
              <div className={s.colEmpty}>
                {search.trim() ? `No students match "${search.trim()}".` : "No students enrolled yet."}
              </div>
            ) : filtered.map(st => {
              const name = studentName(st);
              return (
                <button key={st.id} type="button" className={s.studentRow} onClick={() => onOpenStudent(st)}>
                  <span className={s.avatar} style={{ background: avatarColor(name) }}>
                    {st.photoUrl ? <img src={st.photoUrl} alt="" className={s.avatarImg} /> : initials(name)}
                  </span>
                  <span className={s.studentMain}>
                    <span className={s.studentName}>{name}</span>
                    <span className={s.studentMeta}>{st.studentNumber ? `#${st.studentNumber}` : ""}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
